import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

/**
 * ⚠ THE FRAMEWORK'S OWN STRUCTURE, decomposed — and it is a DECOMPOSITION, not a second
 * computation. Every `requirement_status` here is the same value the framework card rolls up and
 * the Audit Center counts; the API emits it once from the `classified` CTE and every surface
 * reads it. If this view could disagree with the card, it would be computing its own answer,
 * which is precisely the drift that had the card saying "implemented" while the Audit Center said
 * "met" about the same rows.
 */

/** ⚠ The framework's OWN level name — function, category, clause. Never one of ours. */
export interface FrameworkNode {
  node_code: string;
  parent_code: string | null;
  node_type: string;
  name: string;
  sort_order: number;
}

export interface RequirementControl {
  control_reference: string;
  display_code: string;
  title: string;
  /** ⚠ supports / partial / satisfies — the claim's TYPE. Not a satisfaction claim. */
  relationship_type: string;
  /** ⚠ THE CONTROL'S OWN owner. NULL when nobody has taken it — an honest absence. */
  owner_name: string | null;
  /** ⚠ THE CONTROL'S OWN evidence. Evidence attaches to control work, never to a requirement. */
  evidence_count: number;
}

export interface HierarchyRequirement {
  requirement_reference: string;
  display_code: string;
  title: string;
  parent_code: string | null;
  sort_order: number;
  /**
   * ⚠ REQUIREMENTS ARE MET; CONTROLS ARE IMPLEMENTED. NULL when the requirement is off the status
   * axis entirely — an unmapped requirement has no status, it has a coverage gap.
   */
  requirement_status: 'met' | 'partially_met' | 'not_met' | 'not_assessed' | null;
  status_axis: 'status' | 'coverage' | 'scope' | 'excluded';
  /** ⚠ Kept for the DETAIL: which partial it is decides the next step. */
  bucket: string;
  controls: RequirementControl[];
}

export interface FrameworkHierarchy {
  reference_id: string;
  display_code: string;
  name: string;
  nodes: FrameworkNode[];
  requirements: HierarchyRequirement[];
}

export function useFrameworkHierarchy(reference: string | undefined) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-frameworks', 'hierarchy', tenantId, reference],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/compliance/workspace/frameworks/${reference}/hierarchy`);
      return data.data as FrameworkHierarchy;
    },
    enabled: !!tenantId && !!reference,
  });
}

/** ⚠ Requirement vocabulary, in one place, so no surface invents its own wording. */
export const REQUIREMENT_STATUS_LABEL: Record<string, string> = {
  met: 'Met',
  partially_met: 'Partially Met',
  not_met: 'Not Met',
  not_assessed: 'Not Assessed',
};

/**
 * ⚠ THE TWO PARTIALS LEAD TO DIFFERENT NEXT STEPS, which is why the raw bucket survives:
 * a coverage gap means map more controls; an implementation gap means finish the work.
 * The headline collapses them; the detail must not.
 */
export function partialDetail(bucket: string): string | null {
  if (bucket === 'partially_covered') return 'coverage gap — map more controls';
  if (bucket === 'partially_implemented') return 'implementation gap — finish implementing';
  return null;
}

/**
 * ⚠ OWNERSHIP IS AGGREGATED, NEVER ASSERTED. A requirement has no owner — its mapped controls do.
 * The shape follows the CARDINALITY so the row can never imply a single responsible person when
 * several exist, which is exactly the fabrication a mockup's "D. Chen" invited.
 *
 * ⓘ Unowned controls are counted as unowned rather than ignored: "1 owner across 3" would be a
 * lie if two of the three have nobody, so the caller sees the mapped count and can expand.
 */
export function ownerSummary(controls: RequirementControl[]): string {
  if (controls.length === 0) return '—';
  const named = controls.map((c) => c.owner_name).filter((n): n is string => Boolean(n));
  const distinct = [...new Set(named)];
  if (distinct.length === 0) return 'Unassigned';
  if (controls.length === 1) return distinct[0];
  if (distinct.length === 1) {
    return named.length === controls.length
      ? `1 owner across ${controls.length}`
      : `1 owner across ${named.length} of ${controls.length}`;
  }
  return `${distinct.length} owners`;
}

/**
 * ⚠ THE EVIDENCE ROLLUP NAMES ITS SOURCE. A bare number beside a requirement reads as evidence
 * FOR the requirement, which does not exist — evidence is produced against control work, and the
 * wording has to carry that or the aggregate becomes a claim of its own.
 */
export function evidenceSummary(controls: RequirementControl[]): string {
  const withEvidence = controls.filter((c) => c.evidence_count > 0);
  const total = controls.reduce((a, c) => a + c.evidence_count, 0);
  if (total === 0) return 'No evidence yet';
  return `${total} item${total === 1 ? '' : 's'} across ${withEvidence.length} mapped control${withEvidence.length === 1 ? '' : 's'}`;
}
